import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCurrentStore,
  sendTestEmail,
  sendTestSms,
  updateCurrentStore,
} from '@/lib/api/stores.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { GoogleReviewsSettingsSection } from '@/pages/settings/GoogleReviewsSettingsSection.js';
import { nullableText } from '@/pages/settings/settings-utils.js';
import { getM360Status } from '@/lib/api/m360.js';

const COMING_SOON = ['QuickBooks', 'Yeastar PBX', 'Parts Suppliers', 'N8N Webhooks'];

export function IntegrationsSettingsPage() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const m360StatusQ = useQuery({ queryKey: ['m360-status'], queryFn: getM360Status });
  const store = storeQuery.data;
  const [telnyxPhone, setTelnyxPhone] = useState('');
  const [emailFromName, setEmailFromName] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [m360Auto, setM360Auto] = useState(false);
  const [m360Portal, setM360Portal] = useState(true);

  useEffect(() => {
    if (!store) return;
    setTelnyxPhone(store.telnyxPhoneNumber ?? '');
    setEmailFromName(store.emailFromName ?? store.name ?? '');
    setM360Auto(Boolean(store.m360AutoRetrieveOnImei));
    setM360Portal(store.m360ShowInPortal !== false);
  }, [store]);

  const saveM360 = useMutation({
    mutationFn: () =>
      updateCurrentStore({
        m360AutoRetrieveOnImei: m360Auto,
        m360ShowInPortal: m360Portal,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('M360 settings saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const saveTelnyx = useMutation({
    mutationFn: () => updateCurrentStore({ telnyxPhoneNumber: nullableText(telnyxPhone) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Telnyx settings saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const saveEmail = useMutation({
    mutationFn: () => updateCurrentStore({ emailFromName: nullableText(emailFromName) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Email settings saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const testSmsMutation = useMutation({
    mutationFn: (phone: string) => sendTestSms({ phone }),
    onSuccess: (r) => toastSuccess(`Test SMS sent to ${r.to}`),
    onError: (e) => toastError(parseApiError(e).message),
  });

  const testEmailMutation = useMutation({
    mutationFn: (email: string) => sendTestEmail({ email }),
    onSuccess: (r) => toastSuccess(`Test email sent to ${r.to}`),
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">📱 Telnyx</h2>
        <p className="mt-1 text-sm text-slate-500">API Key: Configured in server .env ✓</p>
        <label className="mt-4 block">
          <span className="text-sm font-medium">From Number</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={telnyxPhone}
            onChange={(e) => setTelnyxPhone(e.target.value)}
            placeholder="+15175550001"
          />
        </label>
        <label className="mt-3 block">
          <span className="text-sm font-medium">Test SMS recipient</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
          />
        </label>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={saveTelnyx.isPending}
            onClick={() => saveTelnyx.mutate()}
          >
            Save
          </button>
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50"
            disabled={testSmsMutation.isPending || testPhone.trim().length < 7}
            onClick={() => testSmsMutation.mutate(testPhone.trim())}
          >
            Send Test SMS
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">📧 Resend</h2>
        <p className="mt-1 text-sm text-slate-500">API Key: Configured in server .env ✓</p>
        <label className="mt-4 block">
          <span className="text-sm font-medium">From Name</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={emailFromName}
            onChange={(e) => setEmailFromName(e.target.value)}
          />
        </label>
        <label className="mt-3 block">
          <span className="text-sm font-medium">Test email</span>
          <input
            type="email"
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
          />
        </label>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={saveEmail.isPending}
            onClick={() => saveEmail.mutate()}
          >
            Save
          </button>
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50"
            disabled={testEmailMutation.isPending || !testEmail.includes('@')}
            onClick={() => testEmailMutation.mutate(testEmail.trim())}
          >
            Send Test Email
          </button>
        </div>
      </section>

      <GoogleReviewsSettingsSection />

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">📊 M360</h2>
        <p className="mt-2 text-sm">
          Status:{' '}
          {m360StatusQ.data?.configured ? (
            <span className="font-semibold text-emerald-700">🟢 Connected</span>
          ) : (
            <span className="font-semibold text-rose-700">🔴 Not configured</span>
          )}
        </p>
        {!m360StatusQ.data?.configured ? (
          <p className="mt-1 text-sm text-slate-500">Add M360_API_KEY to server .env</p>
        ) : (
          <p className="mt-1 text-sm text-slate-500">
            API URL: {m360StatusQ.data.apiUrl ?? '—'}
          </p>
        )}
        <label className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>
            Auto-retrieve on IMEI lookup
            <span className="mt-0.5 block text-xs font-normal text-slate-500">
              When staff looks up an IMEI, auto-fetch latest M360 diagnostic
            </span>
          </span>
          <input
            type="checkbox"
            checked={m360Auto}
            onChange={(e) => setM360Auto(e.target.checked)}
          />
        </label>
        <label className="mt-2 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>
            Show in customer portal
            <span className="mt-0.5 block text-xs font-normal text-slate-500">
              Show diagnostic grade to customers
            </span>
          </span>
          <input
            type="checkbox"
            checked={m360Portal}
            onChange={(e) => setM360Portal(e.target.checked)}
          />
        </label>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
            onClick={() => {
              void m360StatusQ.refetch().then((r) => {
                if (r.data?.configured) toastSuccess('M360 connected');
                else toastError('M360 not configured');
              });
            }}
          >
            Test Connection
          </button>
          <button
            type="button"
            disabled={saveM360.isPending}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => saveM360.mutate()}
          >
            {saveM360.isPending ? 'Saving…' : 'Save M360 Settings'}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">🖨 PrintNode</h2>
        <p className="mt-1 text-sm text-slate-500">API Key: Configured in server .env ✓</p>
        <Link to="/settings/printing" className="mt-3 inline-block text-sm font-semibold text-primary">
          Manage Printers →
        </Link>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">📱 IMEI.info</h2>
        <p className="mt-1 text-sm text-slate-500">API Key: Configured in server .env ✓</p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Coming Soon</h2>
        <ul className="mt-4 space-y-2">
          {COMING_SOON.map((name) => (
            <li key={name} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm">
              <span>{name}</span>
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">
                Coming Soon
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
